import { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'

import { Button, Card, Skeleton } from '@repo/ui'
import { SelectField } from 'components'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { SegmentedTabs } from 'components/navigation/SegmentedTabs/SegmentedTabs'
import { useSeoRecommendations, useSeoStatus } from 'hooks/api'
import { COMPETITOR_RESEARCH_TITLE, ROUTES } from 'routes'

import { RANGE_PRESETS, rangeForPreset, type RangePreset } from '../../websiteAnalyticsModel'
import { SectionNote } from '../shared'
import { SeoAeoCard } from './SeoAeoCard'
import { SeoAlertsBanner } from './SeoAlertsBanner'
import { SeoGapsCard } from './SeoGapsCard'
import { SeoGscCard } from './SeoGscCard'
import { SeoHowItWorks } from './SeoHowItWorks'
import { SeoKeywordsCard } from './SeoKeywordsCard'
import { SeoPageMetaCard } from './SeoPageMetaCard'
import { SeoPagesCard } from './SeoPagesCard'
import { SeoRankingsCard } from './SeoRankingsCard'
import { SeoRecommendationsCard } from './SeoRecommendationsCard'
import { SeoReviewsCard } from './SeoReviewsCard'
import { SeoStatusCard } from './SeoStatusCard'
import { marketLabel } from './seoModel'

type Section =
  | 'recommendations'
  | 'reviews'
  | 'search-console'
  | 'rankings'
  | 'keywords'
  | 'competitors'
  | 'pages'
  | 'page-meta'
  | 'gaps'
  | 'aeo'
  | 'jobs'

const SECTIONS: ReadonlyArray<{
  value: Section
  label: string
  uses: 'market' | 'range' | null
}> = [
  // The loop's two decision surfaces lead (Spencer, 2026-10-03): the review email links here.
  { value: 'recommendations', label: 'Recommendations', uses: null },
  { value: 'search-console', label: 'Search Console', uses: 'range' },
  { value: 'rankings', label: 'Rankings', uses: 'market' },
  { value: 'keywords', label: 'Keywords', uses: 'market' },
  // VRG-744 — the competitor list and profiles moved to Competitor research (sidebar → Research).
  // The tab stays so its proposed count is still visible here and old `?seo=competitors` links
  // land on a pointer rather than on Recommendations; choosing it goes straight there.
  { value: 'competitors', label: 'Competitors', uses: null },
  { value: 'pages', label: 'Pages to leads', uses: 'range' },
  // VRG-695: what search and chat apps read off each live page. No market, no range.
  // The tab says it short; the card carries the full name ("Page titles & link previews").
  { value: 'page-meta', label: 'Titles & previews', uses: null },
  { value: 'gaps', label: 'Gap finder', uses: 'market' },
  { value: 'aeo', label: 'AI answers', uses: null },
  { value: 'reviews', label: 'Review history', uses: null },
  { value: 'jobs', label: 'Jobs and spend', uses: null },
]

const RANGE_OPTIONS: readonly ViewToggleOption<RangePreset>[] = RANGE_PRESETS.map((p) => ({
  value: p.value,
  label: p.label,
}))

/**
 * VRG-643 — SEO & AEO. Built as a tab of `/internal/analytics`; since VRG-672 it is the
 * body of its own page, `/internal/gtm/seo` (GtmSeoPage), and the old tab address
 * redirects there.
 *
 * Every figure here is read from Vera's own tables, which scheduled jobs fill (Search
 * Console nightly, rankings in every market and AI answers on one biweekly Thursday slot
 * (VRG-676), keyword metrics monthly). Opening this page never spends a vendor credit; only a platform admin pressing
 * a run or pull button does, and every such call is checked against the monthly cap first.
 *
 * The section and the market live in the URL (`?seo=`, `?market=`) so a view is linkable.
 * `?range=` uses the same presets as the Analytics Website panel, so a range read off one
 * page means the same days on the other.
 * Each control shows only on the sections it affects — a market picker over Search
 * Console (one property, every country) would claim a filter that does nothing.
 */
export const SeoPanel = () => {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const { data: status, isLoading, error } = useSeoStatus()
  const { data: recs } = useSeoRecommendations()

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params)
    next.set(key, value)
    setParams(next, { replace: true })
  }

  const section: Section = SECTIONS.some((s) => s.value === params.get('seo'))
    ? (params.get('seo') as Section)
    : 'recommendations'
  const markets = (status?.markets ?? []).filter((m) => m.active)
  const market = markets.some((m) => m.code === params.get('market'))
    ? (params.get('market') as string)
    : markets[0]?.code ?? 'us'
  const preset: RangePreset = RANGE_PRESETS.some((p) => p.value === params.get('range'))
    ? (params.get('range') as RangePreset)
    : '30d'
  const range = useMemo(() => rangeForPreset(preset), [preset])
  const uses = SECTIONS.find((s) => s.value === section)?.uses ?? null

  const control =
    uses === 'market' ? (
      <Box sx={{ minWidth: 220 }}>
        <SelectField
          value={market}
          onChange={(v) => set('market', v as string)}
          options={markets.map((m) => ({
            value: m.code,
            label: marketLabel(m),
          }))}
        />
      </Box>
    ) : uses === 'range' ? (
      <ViewToggle
        options={RANGE_OPTIONS}
        value={preset}
        onChange={(v) => set('range', v)}
        ariaLabel="Date range"
      />
    ) : null

  return (
    <Stack spacing={6}>
      {/* VRG-729: an open pull / account / balance incident leads the page, on every section. */}
      <SeoAlertsBanner />
      <SeoHowItWorks schedule={status?.schedule} />
      {/* The section bar takes the full row: with ten sections there is no room beside
          it for a control, and one squeezed in overlaps the last tabs at 1280px. */}
      <SegmentedTabs
        tabs={SECTIONS.map(({ value, label }) => ({
          value,
          // The proposed count rides on the tab so a review queue is visible from any section.
          label:
            value === 'competitors' && (status?.competitorCounts?.proposed ?? 0) > 0
              ? `${label} · ${status?.competitorCounts.proposed}`
              : value === 'recommendations' && (recs?.counts.open ?? 0) > 0
                ? `${label} · ${recs?.counts.open}`
                : label,
        }))}
        value={section}
        onChange={(v) => (v === 'competitors' ? navigate(ROUTES.INTERNAL_COMPETITORS) : set('seo', v))}
      />

      {isLoading ? (
        <Card>
          <Skeleton height={14} lines={8} />
        </Card>
      ) : error || !status ? (
        <Card>
          <SectionNote>
            Could not load SEO status: {(error as Error | null)?.message ?? 'no response'}. Reload,
            or check the API is running.
          </SectionNote>
        </Card>
      ) : (
        <>
          {control ? (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ md: 'center' }}
              spacing={3}
            >
              <Typography variant="body3" color="text.secondary">
                {uses === 'range'
                  ? `${range.since} to ${range.until} (UTC). Search Console lags two to three days, so the last days of a range are thin.`
                  : 'Google results and keyword data for one country at a time.'}
              </Typography>
              {control}
            </Stack>
          ) : null}
          {section === 'recommendations' ? <SeoRecommendationsCard /> : null}
          {section === 'reviews' ? <SeoReviewsCard /> : null}
          {section === 'search-console' ? <SeoGscCard range={range} /> : null}
          {section === 'rankings' ? (
            <SeoRankingsCard market={market} competitors={status.competitors} status={status} />
          ) : null}
          {section === 'keywords' ? <SeoKeywordsCard market={market} status={status} /> : null}
          {section === 'competitors' ? (
            <Card title="Competitors moved">
              <Stack spacing={4} sx={{ mt: 4 }} alignItems="flex-start">
                <SectionNote>
                  The competitor list, profiles and the review of proposed competitors are on {COMPETITOR_RESEARCH_TITLE},
                  with each competitor&rsquo;s pricing, features, posts and search overlap. Rankings and the gap finder stay
                  here.
                </SectionNote>
                <Button variant="primary" component={RouterLink} to={ROUTES.INTERNAL_COMPETITORS}>
                  Open {COMPETITOR_RESEARCH_TITLE}
                </Button>
              </Stack>
            </Card>
          ) : null}
          {section === 'pages' ? <SeoPagesCard range={range} /> : null}
          {section === 'page-meta' ? <SeoPageMetaCard /> : null}
          {section === 'gaps' ? (
            <SeoGapsCard
              key={market}
              market={market}
              competitors={status.competitors}
              taxonomy={status.taxonomy}
            />
          ) : null}
          {section === 'aeo' ? <SeoAeoCard /> : null}
          {section === 'jobs' ? <SeoStatusCard status={status} /> : null}
        </>
      )}
    </Stack>
  )
}
